import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';

/** Coverage figures, as the backend's coverageReport gives them; null when unmeasured. */
interface CoverageFigures {
  coverage: number | null;
  lineCoverage: number | null;
  branchCoverage: number | null;
  linesToCover: number | null;
  uncoveredLines: number | null;
  conditionsToCover: number | null;
  uncoveredConditions: number | null;
}

interface CoveragePart extends CoverageFigures {
  path: string;
}

interface ProjectCoverage extends CoverageFigures {
  project: string;
  parts: CoveragePart[];
  lastAnalysis: string | null;
}

const percent = (value: number | null) => (value === null ? '–' : `${value.toFixed(1)}%`);
const count = (value: number | null) => (value === null ? '–' : value.toLocaleString());

const coverageColor = (value: number | null) => {
  if (value === null) return 'text-gray-400';
  if (value >= 80) return 'text-scarf-green';
  if (value >= 50) return 'text-brand-blue-light';
  return 'text-brand-red';
};

const barColor = (value: number) => {
  if (value >= 80) return 'bg-scarf-green';
  if (value >= 50) return 'bg-brand-blue-light';
  return 'bg-brand-red';
};

/**
 * Test coverage, from SonarQube.
 *
 * This tab used to show three hardcoded projects at 90% with invented file
 * lists and trends. It now shows the coverage SonarQube holds: for each
 * project, overall and for each top-level directory. SonarQube only has
 * coverage once CI's sonarcloud job sends it, so until then the tab says
 * there is none rather than showing a number.
 */
const CoverageComponent: React.FC = () => {
  const [projects, setProjects] = useState<ProjectCoverage[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchCoverageData();
  }, []);

  const fetchCoverageData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiService.getCoverageData();
      const data = response.data as { projects?: ProjectCoverage[] } | undefined;
      setProjects(data?.projects ?? []);
      setLoaded(true);
    } catch (err) {
      // The server's own reason, e.g. "SonarQube is unavailable: …".
      setError(err instanceof Error ? err.message : 'Could not reach the server to load coverage');
      setProjects([]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <div className="flex flex-wrap justify-between items-center gap-4">
          <h3 className="text-xl font-title font-bold text-deep-navy">Test Coverage</h3>
          <button
            onClick={fetchCoverageData}
            disabled={isLoading}
            className="bg-brand-blue-light hover:bg-brand-blue disabled:bg-brand-blue-light/50 text-white font-bold px-6 py-2 rounded-xl transition-colors"
          >
            {isLoading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>

        {error && (
          <div role="alert" className="mt-4 bg-red-50 border border-red-200 text-brand-red rounded-xl p-4">
            {error}
          </div>
        )}

        {!error && isLoading && !loaded && <p className="mt-4 text-gray-500">Loading coverage…</p>}

        {!error && loaded && projects.length === 0 && (
          <p className="mt-4 text-gray-500">SonarQube has no projects.</p>
        )}
      </div>

      {projects.map((project) => (
        <section
          key={project.project}
          aria-labelledby={`coverage-${project.project}`}
          className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm"
        >
          <div className="flex flex-wrap justify-between items-start gap-4 mb-4">
            <div>
              <h4 id={`coverage-${project.project}`} className="text-lg font-title font-bold text-deep-navy break-all">
                {project.project}
              </h4>
              <p className="text-gray-500 text-sm">
                {project.lastAnalysis
                  ? `Last analysed ${new Date(project.lastAnalysis).toLocaleString()}`
                  : 'Never analysed'}
              </p>
            </div>
            <div className={`text-4xl font-bold ${coverageColor(project.coverage)}`} aria-label="Overall coverage">
              {percent(project.coverage)}
            </div>
          </div>

          {project.coverage === null ? (
            <p className="text-gray-600">
              SonarQube has no coverage for this project yet. It arrives once CI&apos;s SonarCloud scan is
              running: a <code className="font-mono">SONAR_TOKEN</code> secret in GitHub, and Automatic
              Analysis turned off in SonarCloud.
            </p>
          ) : (
            <>
              <dl className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {[
                  ['Line coverage', percent(project.lineCoverage)],
                  ['Branch coverage', percent(project.branchCoverage)],
                  ['Lines to cover', count(project.linesToCover)],
                  ['Uncovered lines', count(project.uncoveredLines)],
                ].map(([label, value]) => (
                  <div key={label}>
                    <dt className="text-gray-500 text-sm">{label}</dt>
                    <dd className="text-lg font-bold text-gray-800">{value}</dd>
                  </div>
                ))}
              </dl>

              {project.parts.length > 0 && (
                <ul className="space-y-3" aria-label="Coverage by directory">
                  {project.parts.map((part) => (
                    <li key={part.path}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-mono text-gray-800">{part.path}</span>
                        <span className={`font-bold ${coverageColor(part.coverage)}`}>
                          {percent(part.coverage)}
                          <span className="text-gray-500 font-normal">
                            {' '}· {count(part.uncoveredLines)} of {count(part.linesToCover)} lines uncovered
                          </span>
                        </span>
                      </div>
                      <div className="h-2 bg-frost-white rounded-full overflow-hidden">
                        <div
                          className={`h-full ${barColor(part.coverage ?? 0)}`}
                          style={{ width: `${Math.min(100, Math.max(0, part.coverage ?? 0))}%` }}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </section>
      ))}
    </div>
  );
};

export default CoverageComponent;
